/**
 * Typed wrapper for the Governance contract.
 *
 * Storage layout MUST match `governance.tolk :: GovernanceStorage`.
 *
 * Lifecycle:
 *   1. Anyone calls `sendPropose(payload)`. Returns the assigned id off
 *      the contract via `getProposalNonce` (id = nonce - 1 after the tx).
 *   2. Voters call `sendVote(proposalId, support)`. Gov asks veTONADO
 *      for their current vp; vp arrives in a few seconds via reply.
 *   3. After votingPeriod, anyone calls `sendQueue(proposalId)`.
 *   4. After timelockEnd, anyone calls `sendExecute(proposalId)`.
 *
 * Build proposal payloads via `buildProposalPayload({ dest, value, body })`.
 */
import { Address, beginCell, SendMode, toNano, } from "@ton/core";
import { JETTON_BURN_ADDRESS } from "./tonadoJetton";
export const GOVERNANCE_OPS = {
    VOTING_POWER_REPLY: 0xded05f01,
    PROPOSE: 0xded05f10,
    VOTE: 0xded05f11,
    QUEUE: 0xded05f12,
    EXECUTE: 0xded05f13,
    ADMIN_SET_PARAMS: 0xded05f14,
    ADMIN_SET_VETONADO: 0xded05f15,
    ADMIN_RENOUNCE: 0xded05f16,
    // Permissionless cleanup for orphaned pending vote records (proposal
    // moved out of ACTIVE before the VotingPowerReply landed).
    CLEANUP_PENDING: 0xded05f17,
    ADMIN_PAUSE: 0xded057f1,
    ADMIN_UNPAUSE: 0xded057f2,
    // External-out log emitted on every successful Execute.
    EXECUTE_LOG: 0xded05fa1,
    // External-out log emitted on AdminSetVeTonado swap.
    VE_TONADO_SWAP_LOG: 0xded05fa2,
};
export const PROPOSAL_STATUS = {
    ACTIVE: 0,
    QUEUED: 1,
    EXECUTED: 2,
    REJECTED: 3,
    NONE: 255,
};
/** Build the proposal payload cell — the outbound message the
 *  governance contract will fire when Execute lands. */
export function buildProposalPayload(opts) {
    return beginCell()
        .storeAddress(opts.dest)
        .storeCoins(opts.value)
        .storeRef(opts.body)
        .endCell();
}
export class Governance {
    address;
    init;
    constructor(address, init) {
        this.address = address;
        this.init = init;
    }
    static at(address) {
        return new Governance(address);
    }
    static BURN_ADDRESS = JETTON_BURN_ADDRESS;
    /**
     * Build initial data.
     *
     * Layout (inline):
     *   bool      paused
     *   address   owner
     *   address   veTonadoAddr
     *   uint32    proposalNonce
     *   uint64    nextRequestId
     *   Maybe ref proposals (dict, null at genesis)
     *   Maybe ref pendingVotes (dict, null at genesis)
     *   Maybe ref votedSet (dict, null at genesis)
     *   ref       params → GovParams
     */
    static buildInitialData(opts) {
        const params = beginCell()
            .storeUint(opts.votingPeriodSeconds, 32)
            .storeUint(opts.timelockSeconds, 32)
            .storeUint(opts.quorum, 256)
            .endCell();
        return beginCell()
            .storeUint(opts.paused ? 1 : 0, 1)
            .storeAddress(opts.owner)
            .storeAddress(opts.veTonadoAddr)
            .storeUint(0, 32)
            .storeUint(0, 64)
            .storeUint(0, 1) // proposals = null
            .storeUint(0, 1) // pendingVotes = null
            .storeUint(0, 1) // votedSet = null
            .storeRef(params)
            .endCell();
    }
    static fromInit(opts) {
        const data = Governance.buildInitialData(opts);
        const stateInitCell = beginCell()
            .storeUint(0, 2)
            .storeUint(3, 2)
            .storeRef(opts.code)
            .storeRef(data)
            .storeUint(0, 1)
            .endCell();
        return new Governance(new Address(opts.workchain ?? 0, stateInitCell.hash()), { code: opts.code, data });
    }
    async sendPropose(provider, via, opts) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.PROPOSE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeRef(opts.payload)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendVote(provider, via, opts) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.VOTE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.proposalId, 32)
            .storeUint(opts.support, 1)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.1"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendQueue(provider, via, opts) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.QUEUE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.proposalId, 32)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendExecute(provider, via, opts) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.EXECUTE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.proposalId, 32)
            .endCell();
        await provider.internal(via, {
            value: opts.attached,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // Reclaim a pending vote record orphaned by a proposal that moved out
    // of ACTIVE before the VotingPowerReply landed. Permissionless; the
    // handler verifies the underlying proposal is no longer ACTIVE before
    // deleting.
    async sendCleanupPending(provider, via, opts) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.CLEANUP_PENDING, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.requestId, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.03"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminSetParams(provider, via, opts) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.ADMIN_SET_PARAMS, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.votingPeriodSeconds, 32)
            .storeUint(opts.timelockSeconds, 32)
            .storeUint(opts.quorum, 256)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminSetVeTonado(provider, via, opts) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.ADMIN_SET_VETONADO, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.veTonadoAddr)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminRenounce(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.ADMIN_RENOUNCE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminPause(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.ADMIN_PAUSE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminUnpause(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(GOVERNANCE_OPS.ADMIN_UNPAUSE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // ---- Get methods ----
    async getOwner(provider) {
        const res = await provider.get("getOwner", []);
        return res.stack.readAddress();
    }
    async getVeTonado(provider) {
        const res = await provider.get("getVeTonado", []);
        return res.stack.readAddress();
    }
    async getProposalNonce(provider) {
        const res = await provider.get("getProposalNonce", []);
        return res.stack.readNumber();
    }
    async getVotingPeriodSeconds(provider) {
        const res = await provider.get("getVotingPeriodSeconds", []);
        return res.stack.readNumber();
    }
    async getTimelockSeconds(provider) {
        const res = await provider.get("getTimelockSeconds", []);
        return res.stack.readNumber();
    }
    async getQuorum(provider) {
        const res = await provider.get("getQuorum", []);
        return res.stack.readBigNumber();
    }
    async isPaused(provider) {
        const res = await provider.get("isPaused", []);
        return res.stack.readNumber() !== 0;
    }
    async getProposal(provider, proposalId) {
        const res = await provider.get("getProposal", [
            { type: "int", value: BigInt(proposalId) },
        ]);
        return {
            status: res.stack.readNumber(),
            votingEnd: res.stack.readNumber(),
            timelockEnd: res.stack.readNumber(),
            forVotes: res.stack.readBigNumber(),
            againstVotes: res.stack.readBigNumber(),
            proposer: res.stack.readAddress(),
            createdAt: res.stack.readNumber(),
        };
    }
    async getProposalPayload(provider, proposalId) {
        const res = await provider.get("getProposalPayload", [
            { type: "int", value: BigInt(proposalId) },
        ]);
        return {
            dest: res.stack.readAddress(),
            value: res.stack.readBigNumber(),
            body: res.stack.readCell(),
        };
    }
}
//# sourceMappingURL=governance.js.map